// Video controls
const handleVideo = ( video, shouldPlay ) => {
  if ( !video ) return;
  
  video.autoplay = shouldPlay;
  video.muted = true;
  
  if ( shouldPlay ) {
    video.play().catch( err => console.error( 'Error playing video:', err ) );
  } else {
    video.pause();
    video.currentTime = 0;
  }
};

// Function to extract h3 and .e-img from content wrapper and create new structure
const createContentDiv = ( slide ) => {
  const contentWrapper = slide.querySelector( '.e-div--content-wrapper' );
  if ( !contentWrapper ) return;

  const h3Element = contentWrapper.querySelector( 'h3' );
  const imgElement = contentWrapper.querySelector( '.e-img' );
  
  if ( !h3Element && !imgElement ) return;

  // Create new div container
  const newContentDiv = document.createElement( 'div' );
  newContentDiv.className = 'extracted-content';
  
  // Clone and append elements to new div
  if ( imgElement ) {
    const clonedImg = imgElement.cloneNode( true );
    newContentDiv.appendChild( clonedImg );
  }
  
  if ( h3Element ) {
    const clonedH3 = h3Element.cloneNode( true );
    newContentDiv.appendChild( clonedH3 );
  }
  
  // Append the new div to swiper-slide
  slide.appendChild( newContentDiv );
  
  return newContentDiv;
};

// Function to force load lazy images
const forceLoadLazyImages = ( slide ) => {
  const mediaWrapper = slide.querySelector( '.e-div--media-wrapper' );
  if ( !mediaWrapper ) return;

  const img = mediaWrapper.querySelector( 'img' );
  if ( !img ) return;

  if ( img.loading === 'lazy' ) {
    img.loading = 'eager';
    const src = img.src;
    img.src = '';
    img.src = src;
  }
};

// Slide management
const handleSlideState = ( slide, isActive ) => {
  if ( isActive ) {
    forceLoadLazyImages( slide );
  }
  slide?.classList?.toggle( 'active', isActive );
  const video = slide?.querySelector( '.e-div--media-wrapper video' );
  handleVideo( video, isActive );
};

// Update all slides
const updateSlides = ( slides, activeSlide ) => {
  slides.forEach( slide => handleSlideState( slide, false ) );
  handleSlideState( activeSlide, true );
};

// Hover handler
const createHoverHandler = ( slides ) => {
  let isTransitioning = false;
  let currentHover = null;

  const handleHover = ( slide ) => {
    if ( isTransitioning || slide.classList.contains( 'active' ) ) return;
    
    isTransitioning = true;
    updateSlides( slides, slide );

    setTimeout( () => {
      isTransitioning = false;
      if ( currentHover && currentHover !== slide ) {
        updateSlides( slides, currentHover );
      }
    }, 800 );
  };

  return {
    handleHover,
    setCurrentHover: ( slide ) => { currentHover = slide; }
  };
};

// Helper function to check if screen size meets requirements for wrapper type
const shouldActivateForScreenSize = ( wrapper ) => {
  const isExpandingCardsWithIcons = wrapper.classList.contains( 'e-stn--expanding-cards-with-icons' );
  const isExpandingCards = wrapper.classList.contains( 'e-stn--expanding-cards' );
  
  if ( isExpandingCardsWithIcons ) {
    return window.matchMedia( '(min-width: 992px)' ).matches;
  } else if ( isExpandingCards ) {
    return window.matchMedia( '(min-width: 1200px)' ).matches;
  }
  
  return false;
};

// Helper function to cleanup wrapper content
const cleanupWrapper = ( wrapper ) => {
  const slides = wrapper.querySelectorAll( '.swiper-slide' );
  slides.forEach( slide => {
    const video = slide.querySelector( '.e-div--media-wrapper video' );
    handleVideo( video, false );
    const existingContent = slide.querySelector( '.extracted-content' );
    if ( existingContent ) {
      existingContent.remove();
    }
  } );
};

// Helper function to setup wrapper content for icons variant
const setupWrapperContent = ( wrapper ) => {
  const slides = wrapper.querySelectorAll( '.swiper-slide' );
  slides.forEach( slide => {
    const existingContent = slide.querySelector( '.extracted-content' );
    if ( existingContent ) {
      existingContent.remove();
    }
    createContentDiv( slide );
  } );
};

// Helper function to process a single wrapper during resize
const processWrapperOnResize = ( wrapper ) => {
  if ( !shouldActivateForScreenSize( wrapper ) ) {
    cleanupWrapper( wrapper );
  } else if ( wrapper.classList.contains( 'e-stn--expanding-cards-with-icons' ) ) {
    setupWrapperContent( wrapper );
  }
};

// Handle resize event
const createResizeHandler = ( cardWrappers ) => {
  let timeoutId;
  return () => {
    clearTimeout( timeoutId );
    timeoutId = setTimeout( () => {
      cardWrappers.forEach( processWrapperOnResize );
    }, 250 );
  };
};

// Setup wrapper functionality
const setupWrapper = ( wrapper ) => {
  const slides = wrapper.querySelectorAll( '.swiper-slide' );
  if ( !slides?.length ) return;

  const hoverHandler = createHoverHandler( slides );
  const isExpandingCardsWithIcons = wrapper.classList.contains( 'e-stn--expanding-cards-with-icons' );

  slides.forEach( slide => {
    const video = slide.querySelector( '.e-div--media-wrapper video' );
    handleVideo( video, false );

    if ( isExpandingCardsWithIcons && shouldActivateForScreenSize( wrapper ) ) {
      createContentDiv( slide );
    }

    slide.addEventListener( 'mouseenter', () => {
      hoverHandler.setCurrentHover( slide );
      hoverHandler.handleHover( slide );
    } );

    slide.addEventListener( 'mouseleave', () => {
      hoverHandler.setCurrentHover( null );
    } );
  } );

  updateSlides( slides, slides[0] );
};

// Initialize on DOM load
document.addEventListener( 'DOMContentLoaded', () => {
  const cardWrappers = document.querySelectorAll( '.e-stn--expanding-cards, .e-stn--expanding-cards-with-icons' );
  if ( !cardWrappers?.length ) return;

  // Filter wrappers that should be activated based on screen size
  const activeWrappers = Array.from( cardWrappers ).filter( wrapper => shouldActivateForScreenSize( wrapper ) );
  if ( !activeWrappers.length ) return;

  try {
    activeWrappers.forEach( setupWrapper );
    window.addEventListener( 'resize', createResizeHandler( cardWrappers ) );
  } catch ( error ) {
    console.error( 'Error initializing expanding cards:', error );
  }
} );